"use client";
import { Link, useNavigate } from "react-router-dom";
import {
  bell,
  dropDown,
  helpCenter,
  location,
  logo,
  menu,
  switchProfile,
  userIcon,
} from "../asset";
import CustomInput from "./CustomInput";
import useDropdown from "../util/hooks/useDropDown";
import Notification from "../pages/notification/Notification";
import { notificationData, searchRouteMap } from "../util/Constants";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import BranchSelector from "./common/BranchSelector";
import {
  formatDateTime,
  getDaysLeft,
  popupNavigation,
} from "../util/functions/UniversalFunctions";
import { useState } from "react";
import { featureIdMap } from "./Sidebar";
import { FiAlertCircle, FiCalendar } from "react-icons/fi";

export default function Navbar({ onMenuClick }) {
  const { isOpen, toggleDropdown, dropdownRef } = useDropdown(false);

  const staff = useSelector((state: RootState) => state.access.staff);

  const school = useSelector((state: RootState) => state.access.schoolBranch);
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const {
    isOpen: isBranchOpen,
    toggleDropdown: toggleBranchDropdown,
    dropdownRef: branchDropdownRef,
  } = useDropdown(false);

  const notifications = notificationData;

  const hasUnread = notifications.some((n) => n.status === "unread");

  const features = useSelector((state: RootState) => state.access.features);
  const user = useSelector((state: RootState) => state.access.staff);
  const subscription = useSelector(
    (state: RootState) => state.access.subscription,
  );

  const isFeatureEnabled = (menuName: string) => {
    if (menuName === "academic") {
      return (
        user?.roleName === "ROLE_ADMIN" || user?.roleName === "ROLE_SUPER_ADMIN"
      );
    }

    const featureId = featureIdMap[menuName];

    if (!featureId) return true;

    return features.some(
      (feature) => feature.featuresId === featureId && feature.active,
    );
  };

  const canViewSubscription =
    user?.roleName === "ROLE_ADMIN" || user?.roleName === "ROLE_SUPER_ADMIN";

  const handleSearchChange = (value: string) => {
    setSearchValue(value);

    const query = value.toLowerCase().trim();

    if (!query) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const matched = Object.keys(searchRouteMap).filter((key) => {
      const matchesQuery = key.toLowerCase().includes(query);
      const hasAccess = isFeatureEnabled(key);
      return matchesQuery && hasAccess;
    });

    setSuggestions(matched);
    setShowSuggestions(true);
  };

  const handleSuggestionClick = (key: string) => {
    navigate(searchRouteMap[key]);
    setSearchValue("");
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const profileMenuItems = [
    {
      label: "View Profile",
      icon: userIcon,
      onClick: "/view-profile",
    },
    // {
    //   label: "Switch Profile",
    //   icon: switchProfile,
    //   onClick: "",
    // },
    {
      label: "Help Center",
      icon: helpCenter,
      onClick: "/help",
    },
    {
      label: "Update Password",
      icon: switchProfile,
      onClick: popupNavigation("updatePassword"),
    },
  ];

  const daysLeft = subscription?.endDate
    ? getDaysLeft(subscription.endDate)
    : null;

  return (
    <nav
      className={
        "w-full sticky bg-white text-center top-0 px-2 py-4 2xl:px-16 xl:p-4 xl:pr-7 flex items-center flex-col max-md:px-[5vw] z-10"
      }
    >
      {/* Nav items */}
      <div className={"flex w-full justify-between items-center gap-4"}>
        <div
          className="relative md:flex gap-3 items-center"
          ref={branchDropdownRef}
        >
          <div
            className="flex gap-11 cursor-pointer"
            onClick={toggleBranchDropdown}
          >
            <div className="flex justify-center items-center xl:hidden">
              <img src={school?.schoolBranchIcon} alt="logo" className="w-32" />
            </div>

            <div className="md:flex gap-3 items-center hidden">
              <BranchSelector />
            </div>
          </div>
        </div>

        <div className="flex gap-6 items-center">
          <div className="relative hidden lg:block w-72">
            <CustomInput
              name="search"
              value={searchValue}
              onChange={(e: any) => handleSearchChange(e.target.value)}
              placeholder="Search..."
              type="search"
              isSearchButton={true}
              inputClassName="outline-none pl-12 bg-background-200 text-sm font-normal placeholder:text-neutral-300 rounded-full border-none py-4 px-5 w-full"
            />

            {showSuggestions && (
              <div className="absolute top-14 w-full bg-white text-start shadow-lg rounded-xl border border-neutral-50 z-50">
                {suggestions.length > 0 ? (
                  suggestions.map((key) => (
                    <div
                      key={key}
                      onClick={() => handleSuggestionClick(key)}
                      className="px-4 py-3 text-sm cursor-pointer hover:bg-background-200 transition-colors"
                    >
                      {key}
                    </div>
                  ))
                ) : (
                  <div className="px-4 py-3 text-sm text-neutral-300 text-center">
                    No Results Found
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="relative shrink-0">
            <button onClick={toggleDropdown} className="relative">
              <img src={bell} alt="bell" className="hidden md:block" />
              {hasUnread && (
                <span className="absolute top-0 hidden md:block right-0 w-3 h-3 bg-error-500 rounded-full border-2 border-white" />
              )}
            </button>

            {isOpen &&
              createPortal(
                <>
                  {/* overlay */}
                  <div
                    className="fixed inset-0 z-[999] bg-black/30"
                    onClick={toggleDropdown}
                  />

                  {/* dropdown */}
                  <div
                    ref={dropdownRef}
                    className="fixed top-16 right-8 z-[1000]"
                  >
                    <Notification notifications={notifications} />
                  </div>
                </>,
                document.body,
              )}
          </div>

          <div className="flex gap-1 lg:gap-3 items-center shrink-0 group">
            {!staff?.profile_image_url ? (
              <div className="flex items-center justify-center rounded-full w-10 h-10 bg-primary-600 text-white font-semibold">
                {staff?.fullName?.charAt(0).toUpperCase() ?? "A"}
              </div>
            ) : (
              <img
                src={staff?.profile_image_url}
                alt="profile"
                className="w-10 h-10 rounded-full object-cover bg-background-200"
              />
            )}

            <div className="flex md:flex-col md:items-start">
              <div className="flex items-center gap-1">
                <p className="text-sm hidden lg:block font-semibold text-neutral-400">
                  {staff?.fullName || "Admin"}
                </p>
                <img src={dropDown} alt="location" className="w-3 h-3" />
              </div>
              <span className="text-xs text-neutral-300 font-normal hidden lg:block">
                {staff?.email}
              </span>
            </div>

            <div className="absolute right-8 lg:right-4 top-20 xl:top-14 px-4 bg-white rounded-xl shadow-xl border border-neutral-50 text-center z-20 opacity-0 group-hover:opacity-100 group-hover:visible invisible transition-opacity duration-200">
              <div className="flex flex-col gap-3 py-4 text-sm text-neutral-600 font-medium">
                {profileMenuItems.map((item) => (
                  <Link
                    to={item.onClick}
                    key={item.label}
                    className=" py-2 cursor-pointer flex items-center gap-3 transition-transform hover:scale-[1.03]"
                  >
                    <img src={item.icon} alt={item.label} className="w-5 h-5" />
                    <span>{item.label}</span>
                  </Link>
                ))}

                {canViewSubscription && (
                  <div
                    className={`flex items-start gap-3 px-3 py-3 rounded-xl transition-all
    ${
      daysLeft === null
        ? "bg-neutral-100 text-neutral-500"
        : daysLeft < 0
          ? "bg-neutral-200 text-neutral-600"
          : daysLeft <= 3
            ? "bg-error-50 text-error-600"
            : daysLeft <= 7
              ? "bg-warning-50 text-warning-700"
              : "bg-primary-50 text-primary-600"
    }`}
                  >
                    {/* Icon */}
                    <div className="mt-1">
                      {daysLeft !== null && daysLeft <= 3 ? (
                        <FiAlertCircle className="w-5 h-5 text-error-500" />
                      ) : (
                        <FiCalendar className="w-5 h-5 text-primary-500" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex flex-col text-left leading-tight">
                      {/* Title */}
                      <span className="text-xs font-medium text-neutral-400">
                        Subscription
                      </span>

                      {/* Main Status */}
                      <span className="text-sm font-semibold text-neutral-800">
                        {daysLeft === null
                          ? "No Active Plan"
                          : daysLeft < 0
                            ? "Expired"
                            : `${daysLeft} days left`}
                      </span>

                      {/* Expiry Date */}
                      {subscription?.endDate && (
                        <span className="text-xs text-neutral-500">
                          Expires on {formatDateTime(subscription.endDate)}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="xl:hidden shrink-0" onClick={onMenuClick}>
            <img src={menu} alt="menu" className="w-8 h-8" />
          </div>
        </div>
      </div>
    </nav>
  );
}
